import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { loadStripe, Stripe, StripeElements } from '@stripe/stripe-js';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import {
  Invoice,
  PaymentIntent,
  StripeCustomer,
} from '../../models/payment.interface';
import {
  CancelSubscriptionResponse,
  createSubscriptionObj,
  CreateSubscriptionResponse,
  ICheckoutPayload,
  SubscriptionValidatorResponse,
} from '../../models/user.subscription.interface';
import { API_ROUTES } from '../../util/route.constants';

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  private stripe: Stripe | null = null;
  private elements: StripeElements | null = null;

  constructor(private readonly http: HttpClient) { }

  loadStripe() {
    return new Promise((resolve) => {
      this.http
        .get<{
          apiKey: string;
        }>(`${environment.cva_url}${API_ROUTES.STRIPE_API_KEY}`)
        .subscribe(async (res: { apiKey: string }) => {
          if (res?.apiKey) {
            this.stripe = await loadStripe(res.apiKey);
            this.elements = this.stripe!.elements();
            resolve(res.apiKey);
          }
        });
    });
  }

  getStripe(): Stripe | null {
    return this.stripe;
  }
  getStripeElements(): StripeElements | null {
    return this.elements;
  }
  createPaymentIntent(
    paymentIntentObj: PaymentIntent
  ): Observable<{ clientSecret: string }> {
    return this.http.post<{ clientSecret: string }>(
      `${environment.cva_url}${API_ROUTES.STRIPE_PAYMENT_INTENT}`,
      paymentIntentObj
    );
  }
  createSubscription(
    subscriptionObj: createSubscriptionObj
  ): Observable<CreateSubscriptionResponse> {
    return this.http.post<CreateSubscriptionResponse>(
      `${environment.cva_url}${API_ROUTES.STRIPE_CREATE_SUBSCRIPTION}`,
      subscriptionObj
    );
  }

  cancelSubscription(date: string): Observable<CancelSubscriptionResponse> {
    return this.http.post<CancelSubscriptionResponse>(
      `${environment.cva_url}${API_ROUTES.STRIPE_CANCEL_SUBSCRIPTION}`,
      { dt2: date }
    );
  }

  pauseSubscription(date: string): Observable<CancelSubscriptionResponse> {
    return this.http.post<CancelSubscriptionResponse>(
      `${environment.cva_url}${API_ROUTES.STRIPE_PAUSE_SUBSCRIPTION}`,
      { dt2: date }
    );
  }

  reactivateSubscription(): Observable<{ response: boolean }> {
    return this.http.post<{ response: boolean }>(
      `${environment.cva_url}${API_ROUTES.STRIPE_RENEW_SUBSCRIPTION}`,
      {}
    );
  }

  updateSubscription(
    subscriptionObj: createSubscriptionObj
  ): Observable<CreateSubscriptionResponse> {
    return this.http.post<CreateSubscriptionResponse>(
      `${environment.cva_url}${API_ROUTES.STRIPE_UPDATE_SUBSCRIPTION}`,
      subscriptionObj
    );
  }
  createCustomer(userDetails: {
    email: string;
    firstName: string;
    lastName: string;
  }): Observable<StripeCustomer> {
    return this.http.post<StripeCustomer>(
      `${environment.cva_url}${API_ROUTES.STRIPE_CREATE_CUSTOMER}`,
      userDetails
    );
  }

  updateCustomerName(userName: string): Observable<StripeCustomer> {
    return this.http.post<StripeCustomer>(
      `${environment.cva_url}${API_ROUTES.STRIPE_UPDATE_CUSTOMER}`,
      { newName: userName }
    )
  }

  getInvoicesByEmail(email: string): Observable<{ response: Invoice[] }> {
    return this.http.get<{ response: Invoice[] }>(
      `${environment.cva_url}${API_ROUTES.PURCHASE_HISTORY}/${email}`
    );
  }
  getCurrentPlan(email: string): Observable<{ response: Invoice }> {
    return this.http.get<{ response: Invoice }>(
      `${environment.cva_url}${API_ROUTES.CURRENT_PLAN}/${email}`
    );
  }

  validateSubscription(
    isDetailRequired: boolean = false
  ): Observable<SubscriptionValidatorResponse | null> {
    return this.http.get<SubscriptionValidatorResponse | null>(
      `${environment.cva_url}${API_ROUTES.VALIDATE_SUBSCRIPTION}?isDetailRequired=${isDetailRequired}`
    );
  }

  manageBilling(): Observable<{ returnUrl: string }> {
    return this.http.post<{ returnUrl: string }>(
      `${environment.cva_url}${API_ROUTES.MANAGE_BILLING}`,
      {}
    );
  }

  createCheckoutSessionForIndividual(subscriptionObj: ICheckoutPayload): Observable<{ url: string }> {
    return this.http.post<{ url: string }>(
      `${environment.cva_url}${API_ROUTES.CHECKOUT_SESSION_INDIVIDUAL}`,
      subscriptionObj
    );
  }

}
